import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, Button,
  CircularProgress, Alert, IconButton, Stack, Divider, Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PaymentsIcon from '@mui/icons-material/Payments';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VerifiedIcon from '@mui/icons-material/Verified';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import { mainApi } from '../../config/apiClient';
import { useAuth } from '../../contexts/AuthContext';
import RenewalPaymentFlow from './RenewalPaymentFlow';
import MobileSubscriptionInfo from './MobileSubscriptionInfo';
import { getBranchRenewalCases, BranchRenewalCase } from '../../services/companyBillingService';

interface OpenRenewalCaseResponse {
  hasCase: boolean;
  token?: string;
  totalAmount?: number;
  currency?: string;
  expiryDate?: string | null;
}

interface ManagerBillingDialogProps {
  open: boolean;
  onClose: () => void;
  /** Aktif şubenin Firebase kimliği (cihaz/ürün yenilemelerini bu şube için çeker). */
  branchId: string | null;
  /** Başlıkta gösterilecek şube adı (opsiyonel). */
  branchName?: string | null;
}

const fmtMoney = (amount?: number, currency?: string) => {
  const n = typeof amount === 'number' ? amount : 0;
  return `${n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency || 'TRY'}`;
};

const fmtDate = (iso?: string | null) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

/**
 * Şube manager paneli lisans/tahsilat popup'ı. İki bölüm:
 *  1. Panel / Lisans — şirket geneli aktif tahsilat (GET /api/Company/open-renewal-case).
 *  2. Şube cihaz/ürün yenilemeleri — aktif şubeye ait aktif tahsilatlar
 *     (GET /api/Company/branch-renewal-cases). Her kayıt ürün + bitiş tarihi + Öde.
 *
 * "Öde" seçilince ilgili token ile paylaşılan RenewalPaymentFlow (PayTR) açılır.
 * Ödeme onaylanınca şirket bilgisi tazelenir ve listeler yeniden çekilir.
 */
const ManagerBillingDialog: React.FC<ManagerBillingDialogProps> = ({ open, onClose, branchId, branchName }) => {
  const { refreshCompany } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [companyCase, setCompanyCase] = useState<OpenRenewalCaseResponse | null>(null);
  const [branchCases, setBranchCases] = useState<BranchRenewalCase[]>([]);

  // Seçilen ödeme token'ı (company ya da branch kaydı). Null → liste görünümü.
  const [activeToken, setActiveToken] = useState<string | null>(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [companyRes, branchRes] = await Promise.all([
        mainApi.get('/api/Company/open-renewal-case').then((r) => r?.data as OpenRenewalCaseResponse),
        getBranchRenewalCases(branchId).catch(() => [] as BranchRenewalCase[]),
      ]);
      setCompanyCase(companyRes ?? null);
      setBranchCases(Array.isArray(branchRes) ? branchRes : []);
    } catch (err) {
      setError('Ödeme bilgisi alınamadı. Lütfen tekrar deneyin.');
      setCompanyCase(null);
      setBranchCases([]);
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => {
    if (open) {
      setActiveToken(null);
      loadAll();
    } else {
      // Kapanışta durumu sıfırla ki tekrar açılınca taze sorgulansın.
      setCompanyCase(null);
      setBranchCases([]);
      setActiveToken(null);
      setError(null);
      setLoading(true);
    }
  }, [open, loadAll]);

  const handlePaid = useCallback(() => {
    // Ödeme onaylandı → şirket bilgisini tazele + listeleri yeniden çek.
    void refreshCompany();
    loadAll();
  }, [refreshCompany, loadAll]);

  const hasCompanyCase = !!companyCase?.hasCase && !!companyCase.token;
  const nothingToPay = !hasCompanyCase && branchCases.length === 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <PaymentsIcon color="primary" />
          <span>Lisans / Ödeme{branchName ? ` — ${branchName}` : ''}</span>
        </Stack>
        <IconButton
          aria-label="Kapat"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <MobileSubscriptionInfo />
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>
        ) : activeToken ? (
          <Box>
            <Button
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={() => setActiveToken(null)}
              sx={{ mb: 1 }}
            >
              Listeye dön
            </Button>
            <RenewalPaymentFlow token={activeToken} onPaid={handlePaid} onClose={onClose} />
          </Box>
        ) : nothingToPay ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Ödeme için sizi bekleyen bir tutar yok
            </Typography>
            <Typography color="text.secondary">
              Lisans veya cihaz/ürün yenilemesi için henüz bir ödeme kaydı oluşturulmamış.
              Kesintisiz kullanım için lütfen bizimle iletişime geçin; tutarınız tanımlandığında
              ödemenizi buradan güvenle tamamlayabilirsiniz.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2.5} sx={{ py: 1 }}>
            {/* 1) Panel / Lisans (şirket geneli) */}
            {hasCompanyCase && (
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <VerifiedIcon fontSize="small" color="success" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Panel / Lisans</Typography>
                </Stack>
                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                    <Box>
                      <Typography sx={{ fontWeight: 600 }}>Panel lisans yenileme</Typography>
                      {fmtDate(companyCase?.expiryDate) && (
                        <Typography variant="body2" color="text.secondary">
                          Son tarih: {fmtDate(companyCase?.expiryDate)}
                        </Typography>
                      )}
                      <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 700 }}>
                        {fmtMoney(companyCase?.totalAmount, companyCase?.currency)}
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      startIcon={<PaymentsIcon />}
                      onClick={() => setActiveToken(companyCase!.token!)}
                    >
                      Öde
                    </Button>
                  </Stack>
                </Box>
              </Box>
            )}

            {hasCompanyCase && branchCases.length > 0 && <Divider />}

            {/* 2) Şube cihaz/ürün yenilemeleri */}
            {branchCases.length > 0 && (
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <PointOfSaleIcon fontSize="small" color="primary" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Şube cihaz / ürün yenilemeleri
                  </Typography>
                </Stack>
                <Stack spacing={1.5}>
                  {branchCases.map((c) => {
                    const productNames = c.lines?.map((l) => l.name).filter(Boolean) ?? [];
                    return (
                      <Box
                        key={c.token}
                        sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                          <Box sx={{ minWidth: 0 }}>
                            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                              <Chip
                                size="small"
                                label={c.sourceType === 'FiscalDevice' ? 'Yazarkasa' : c.sourceType === 'Contract' ? 'Sözleşme' : 'Yenileme'}
                                color={c.sourceType === 'FiscalDevice' ? 'warning' : 'default'}
                              />
                            </Stack>
                            <Typography sx={{ fontWeight: 600 }}>
                              {productNames.length > 0 ? productNames.join(', ') : 'Yenileme'}
                            </Typography>
                            {fmtDate(c.expiryDate) && (
                              <Typography variant="body2" color="text.secondary">
                                Son tarih: {fmtDate(c.expiryDate)}
                              </Typography>
                            )}
                            <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 700 }}>
                              {fmtMoney(c.totalAmount, c.currency)}
                            </Typography>
                          </Box>
                          <Button
                            variant="contained"
                            startIcon={<PaymentsIcon />}
                            onClick={() => setActiveToken(c.token)}
                          >
                            Öde
                          </Button>
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Kapat</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManagerBillingDialog;
